// App.jsx — main shell. Wraps the app in <AuthGate> (Clerk-backed) so the user signs in first.
//
// Pronto redesign (Phase 2): the 13+ legacy tabs are consolidated into a 5-tab
// shell — Today · Coffee · Cook · Kitchen · Ask — rendered in the new Glass
// design system (src/components/pronto.jsx). The existing modules are unchanged
// and reached via each tab's member sub-nav chips; Phase 4 replaces each screen's
// internals with the merged Pronto screens. Everything else (auth, inbox bell,
// ⌘K search, chat drawer, podcast bar, admin gating, hash routing, #chat route)
// is preserved.
import { useState, useEffect, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Coffee as CoffeeIcon, ChefHat, Package, MessageCircle, Bell, Headphones,
} from "lucide-react";
import { useStore } from "./hooks/useStore";
// Today is the default landing view — keep it eager so first paint is instant.
import { Today } from "./modules/Today";
// Every other tab is code-split: its chunk only downloads when the tab is opened.
// Modules are named exports, so unwrap to a default for React.lazy.
const Tonight = lazy(() => import("./modules/Tonight").then(m => ({ default: m.Tonight })));
const Pantry = lazy(() => import("./modules/Pantry").then(m => ({ default: m.Pantry })));
const Appliances = lazy(() => import("./modules/Appliances").then(m => ({ default: m.Appliances })));
const Recipes = lazy(() => import("./modules/Recipes").then(m => ({ default: m.Recipes })));
const Coffee = lazy(() => import("./modules/Coffee").then(m => ({ default: m.Coffee })));
const Inbox = lazy(() => import("./modules/Inbox").then(m => ({ default: m.Inbox })));
const Settings = lazy(() => import("./modules/Settings").then(m => ({ default: m.Settings })));
const Plan = lazy(() => import("./modules/Plan").then(m => ({ default: m.Plan })));
const Shopping = lazy(() => import("./modules/Shopping").then(m => ({ default: m.Shopping })));
const Listen = lazy(() => import("./modules/Listen").then(m => ({ default: m.Listen })));
const Aiden = lazy(() => import("./modules/Aiden").then(m => ({ default: m.Aiden })));
const Bottomless = lazy(() => import("./modules/Bottomless").then(m => ({ default: m.Bottomless })));
const Shop = lazy(() => import("./modules/Shop").then(m => ({ default: m.Shop })));
const Reset = lazy(() => import("./modules/Reset").then(m => ({ default: m.Reset })));
const AdminTasks = lazy(() => import("./modules/AdminTasks").then(m => ({ default: m.AdminTasks })));
import { AuthGate, useCurrentUser } from "./components/Auth";
import { GlobalSearch } from "./components/GlobalSearch";
import { ChatBot, ChatFullPage } from "./components/ChatBot";
import { PodcastPlayerProvider, PodcastPlayerBar, usePodcastPlayer } from "./components/PodcastPlayer";
import { usePodcastAutoRefresh } from "./hooks/usePodcastAutoRefresh";
import { Sidebar, TabBar, Chip } from "./components/pronto";
import { tabLabel, normalizeLocale, IT } from "./lib/i18n";
import { logUsage } from "./lib/usage";

// Module registry — the code-split screens. `label` is the current (legacy) name;
// used for the member sub-nav chips until Phase 4 merges the screens.
const VIEWS = [
  { id: "today", label: "Today", component: Today },
  { id: "plan", label: "Plan", component: Plan },
  { id: "listen", label: "Listen", component: Listen },
  { id: "coffee", label: "Coffee", component: Coffee },
  { id: "aiden", label: "Profiles", component: Aiden },
  { id: "bottomless", label: "Bottomless", component: Bottomless },
  { id: "tonight", label: "Tonight", component: Tonight },
  { id: "recipes", label: "Recipes", component: Recipes },
  { id: "pantry", label: "Pantry", component: Pantry },
  { id: "appliances", label: "Appliances", component: Appliances },
  { id: "shopping", label: "Shopping", component: Shopping },
  { id: "shop", label: "Shop", component: Shop },
  { id: "reset", label: "Reset", component: Reset },
  { id: "inbox", label: "Inbox", component: Inbox },
  { id: "settings", label: "Settings", component: Settings },
  // Hidden admin route — accessible only via direct hash and only when the
  // signed-in user_id is in VITE_ADMIN_USER_IDS. Not surfaced in any nav.
  { id: "admin-tasks", label: "Admin", component: AdminTasks, hidden: true },
];

// The 5-tab IA. `home` is the module a tab lands on; `members` are the screens
// reachable from that tab (via sub-nav chips) until Phase 4 merges them. Settings
// is reached via the profile row, not a tab. `ask` is the chat (uses #chat route).
const TAB_GROUPS = [
  { id: "today", icon: Home, home: "today", members: ["today", "plan"] },
  { id: "coffee", icon: CoffeeIcon, home: "coffee", members: ["coffee", "aiden", "bottomless"] },
  { id: "cook", icon: ChefHat, home: "tonight", members: ["tonight", "recipes"] },
  { id: "kitchen", icon: Package, home: "pantry", members: ["pantry", "appliances", "shopping", "shop", "reset", "inbox"] },
  { id: "listen", icon: Headphones, home: "listen", members: ["listen"] },
  { id: "ask", icon: MessageCircle, home: "ask", members: [] },
];

function viewLabel(id) { return VIEWS.find(v => v.id === id)?.label || id; }
function groupForView(view) {
  return TAB_GROUPS.find(g => g.members.includes(view))?.id
    || (view === "settings" ? "today" : "today");
}

// Shown briefly while a lazily-loaded tab chunk downloads.
function RouteFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <motion.div
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 1.6, repeat: Infinity }}
        className="w-8 h-8 rounded-xl bg-pronto-ink flex items-center justify-center"
      >
        <span className="font-round font-bold text-white text-[15px]">p</span>
      </motion.div>
    </div>
  );
}

const ADMIN_USER_IDS = (import.meta.env.VITE_ADMIN_USER_IDS || "")
  .split(",").map(s => s.trim()).filter(Boolean);
function isAdmin(userId) { return userId && ADMIN_USER_IDS.includes(userId); }

// Build marker so a refresh of `staging` after env-var changes is verifiable.
if (typeof window !== "undefined") {
  console.info(`[pronto] admin allowlist size: ${ADMIN_USER_IDS.length}`);
}

export default function App() {
  return (
    <AuthGate>
      <PodcastPlayerProvider>
        <SignedInApp />
      </PodcastPlayerProvider>
    </AuthGate>
  );
}

function SignedInApp() {
  const { userId, email, ready } = useCurrentUser();
  const store = useStore({ userId, ready });
  const player = usePodcastPlayer();
  const locale = normalizeLocale(store.preferences?.language);
  const [view, setView] = useState(() => {
    const hash = window.location.hash.slice(1);
    const found = VIEWS.find(v => v.id === hash);
    if (!found) return "today"; // Today is the default landing tab
    if (found.hidden && !isAdmin(userId)) return "today"; // refuse hidden routes for non-admins
    return hash;
  });
  const [searchOpen, setSearchOpen] = useState(false);
  // `#chat` renders the standalone full-page chat (also the "Ask" tab in Phase 2)
  // — reached from the chat drawer's expand and the Ask tab. Tracked separately so
  // it can overlay any tab without being a VIEWS entry.
  const [isChatRoute, setIsChatRoute] = useState(() => window.location.hash.slice(1) === "chat");

  useEffect(() => {
    const onHash = () => setIsChatRoute(window.location.hash.slice(1) === "chat");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Keep the hash in sync with the active tab — but not while on the chat route,
  // so entering `#chat` isn't immediately overwritten by the current tab id.
  useEffect(() => { if (!isChatRoute) window.location.hash = view; }, [view, isChatRoute]);

  // Auto-refresh runs at the shell so timers persist across tab navigation.
  usePodcastAutoRefresh({ store, player });

  // Personal usage signal (Adaptive v2) — log each screen view, fire-and-forget.
  useEffect(() => {
    if (userId && !isChatRoute) logUsage(userId, "nav", { view });
  }, [view, userId, isChatRoute]);

  const pendingInbox = (store.pantryInbox || []).filter(p => p.status !== "accepted" && p.status !== "dismissed").length
    + (store.coffeeInbox || []).filter(p => p.status !== "accepted" && p.status !== "dismissed").length
    + (store.recipeInbox || []).filter(p => p.status !== "imported" && p.status !== "dismissed").length;

  // Global search keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Tab navigation: Ask opens the full-page chat; other tabs land on their home.
  const goToTab = (tabId) => {
    if (tabId === "ask") { window.location.hash = "chat"; return; }
    const g = TAB_GROUPS.find(t => t.id === tabId);
    if (g) setView(g.home);
  };

  const ActiveView = VIEWS.find(v => v.id === view);
  const ActiveComponent = (ActiveView?.hidden && !isAdmin(userId)) ? Today : (ActiveView?.component || Today);
  const activeTab = groupForView(view);
  const activeMembers = TAB_GROUPS.find(t => t.id === activeTab)?.members || [];

  const tabs = TAB_GROUPS.map(g => ({ id: g.id, label: tabLabel(g.id, locale), icon: g.icon }));

  if (store.loading) {
    return (
      <div className="min-h-screen bg-pronto-page flex items-center justify-center">
        <motion.div
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-[10px] bg-pronto-logo flex items-center justify-center">
            <span className="font-round font-bold text-white text-[17px]">p</span>
          </div>
          <span className="font-sf text-xs uppercase tracking-[0.18em] text-pronto-text-3">loading pronto</span>
        </motion.div>
      </div>
    );
  }

  if (store.error) {
    return (
      <div className="min-h-screen bg-pronto-page flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-3xl mb-3">⚠️</div>
          <h2 className="font-round font-bold text-xl text-pronto-ink mb-2">Couldn't load Pronto</h2>
          <p className="text-sm text-pronto-text-2 mb-4">{store.error}</p>
          <p className="text-xs text-pronto-text-3">
            If this is your first sign-in, make sure you've run <code className="font-mono bg-white px-1 rounded">supabase/schema.sql</code> in your Supabase SQL editor.
          </p>
        </div>
      </div>
    );
  }

  // Standalone full-page chat (also the Ask tab in Phase 2). Rendered instead of
  // the tab shell; "Back to app" returns to the last tab.
  if (isChatRoute) {
    return <ChatFullPage store={store} onExit={() => { window.location.hash = view || "today"; }} />;
  }

  return (
    <div className="min-h-screen bg-pronto-page font-sf flex flex-col md:flex-row md:gap-6 md:px-8 md:py-8">
      {/* Desktop sidebar */}
      <div className="hidden md:block md:sticky md:top-8 md:self-start">
        <Sidebar
          tabs={tabs}
          activeId={activeTab}
          onSelect={goToTab}
          footer={
            <div className="px-1">
              <button
                onClick={() => setView("settings")}
                className={`w-full text-left font-sf text-[13px] mb-3 ${view === "settings" ? "text-pronto-navy font-semibold" : "text-pronto-text-2"}`}
              >
                Settings{email ? ` · ${email}` : ""}
              </button>
              <div className="font-ny italic text-[12.5px] text-pronto-text-3 leading-relaxed">{IT.proverb}</div>
            </div>
          }
        />
      </div>

      {/* Mobile header */}
      <header className="md:hidden flex items-center justify-between px-5 py-4 bg-[var(--pg-6)] backdrop-blur-[24px] border-b border-[var(--pg-border)] sticky top-0 z-30 safe-top">
        <Brand />
        <NotificationBell count={pendingInbox} active={view === "inbox"} onClick={() => setView("inbox")} />
      </header>

      {/* Desktop: floating bell */}
      <div className="hidden md:block fixed top-8 right-8 z-30">
        <NotificationBell count={pendingInbox} active={view === "inbox"} onClick={() => setView("inbox")} />
      </div>

      <main className="flex-1 w-full md:max-w-[600px] md:mx-auto px-5 md:px-0 py-6 md:py-0 pb-32 md:pb-8 overflow-x-hidden">
        {/* Member sub-nav chips (transitional — until Phase 4 merges each tab's screens) */}
        {activeMembers.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-3">
            {activeMembers.map(mid => (
              <Chip
                key={mid}
                tone={mid === view ? "accent" : "rest"}
                onClick={() => setView(mid)}
                className="whitespace-nowrap flex-shrink-0"
              >
                {viewLabel(mid)}
              </Chip>
            ))}
          </div>
        )}
        <AnimatePresence mode="wait">
          <motion.div
            key={view}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            <Suspense fallback={<RouteFallback />}>
              <ActiveComponent store={store} onNavigate={setView} />
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Mobile bottom tab bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 safe-bottom">
        <TabBar tabs={tabs} activeId={activeTab} onSelect={goToTab} />
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} store={store} onNavigate={setView} />
      <ChatBot store={store} />
      <PodcastPlayerBar store={store} />
    </div>
  );
}

// Pronto brand lockup — the "p" monogram tile (icon seed) + wordmark.
function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative w-9 h-9 rounded-[10px] bg-pronto-logo flex items-center justify-center shadow-sm">
        <span className="font-round font-bold text-white text-[21px] leading-none tracking-[-0.04em]">p</span>
        <span className="absolute bottom-[7px] right-[8px] w-[5px] h-[5px] rounded-full bg-pronto-navy-light" />
      </div>
      <div className="font-round text-[20px] font-bold text-pronto-ink leading-none tracking-[-0.02em]">Pronto</div>
    </div>
  );
}

function NotificationBell({ count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative p-2 rounded-[10px] transition-colors ${
        active ? "text-pronto-navy bg-[rgba(63,74,99,0.12)]" : "text-pronto-text-2 hover:bg-[rgba(63,74,99,0.06)]"
      }`}
      aria-label={count > 0 ? `${count} pending in inbox` : "Inbox"}
    >
      <Bell size={20} strokeWidth={active ? 2.2 : 1.8} />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-pronto-navy text-white text-[10px] font-semibold tabular flex items-center justify-center">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </button>
  );
}
